import { ref, computed } from 'vue'

// 简易静态汇率表（以 CNY 为基准）。实际项目可从后端或远端刷新。
const base = 'CNY'
const currentCurrency = ref(base)
const rates = ref({
  CNY: 1,
  USD: 0.137, // 仅示例
  EUR: 0.128,
  GBP: 0.108,
  JPY: 21.5,
  HKD: 1.07,
  AUD: 0.206,
  CAD: 0.188,
  SGD: 0.185,
  KRW: 187.0
})

const symbols = {
  CNY: '¥',
  USD: '$',
  EUR: '€',
  GBP: '£',
  JPY: '¥',
  HKD: 'HK$',
  AUD: 'A$',
  CAD: 'C$',
  SGD: 'S$',
  KRW: '₩'
}

const locales = {
  CNY: 'zh-CN',
  USD: 'en-US',
  EUR: 'de-DE',
  GBP: 'en-GB',
  JPY: 'ja-JP',
  HKD: 'zh-HK',
  AUD: 'en-AU',
  CAD: 'en-CA',
  SGD: 'en-SG',
  KRW: 'ko-KR'
}

// 将 API 返回的汇率列表转换为本地 rates 结构（以 CNY 为基准：rates[CUR] = 1 CNY = x CUR）
async function fetchRatesFromMxnzp(appId, appSecret, timeoutMs = 5000) {
  const controller = new AbortController()
  const timeout = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const url = `https://www.mxnzp.com/api/exchange_rate/list?app_id=${encodeURIComponent(appId)}&app_secret=${encodeURIComponent(appSecret)}`
    const resp = await fetch(url, { signal: controller.signal })
    clearTimeout(timeout)
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`)
    const json = await resp.json()
    if (json && json.code === 1 && Array.isArray(json.data)) return json.data
    throw new Error('invalid response')
  } catch (err) {
    clearTimeout(timeout)
    throw err
  }
}

export function useCurrency() {
  const code = computed(() => currentCurrency.value)

  const setCurrency = (c) => { if (rates.value[c] != null) currentCurrency.value = c }
  const setRates = (r) => { rates.value = { ...rates.value, ...r } }

  const toDisplay = (valueInBase) => {
    const v = Number(valueInBase) || 0
    const rate = rates.value[code.value] || 1
    return v * rate
  }
  const toBase = (valueInCurrent) => {
    const v = Number(valueInCurrent) || 0
    const rate = rates.value[code.value] || 1
    return rate === 0 ? 0 : v / rate
  }

  const format = (valueInBase, opts = {}) => {
    const v = toDisplay(valueInBase)
    const formatter = new Intl.NumberFormat(locales[code.value] || 'en-US', {
      style: 'currency',
      currency: code.value,
      // 某些自定义币种符号不在标准 ISO 里时，用自定义拼接
      ...opts
    })
    try {
      return formatter.format(v)
    } catch {
      // 回退到手动符号拼接
      const sym = symbols[code.value] || ''
      return `${sym}${v.toLocaleString()}`
    }
  }

  // 后台刷新函数：从第三方 API 获取热门汇率并更新本地 rates
  // API 返回示例为 { from: 'USD', to: 'CNY', price: '6.4608' } 表示 1 USD = 6.4608 CNY
  // 我们的 rates 定义是 1 CNY = rates[CUR] CUR，所以转换关系为 rates[CUR] = 1 / price
  const refreshRates = async (opts = {}) => {
    const { appId, appSecret, timeoutMs = 5000 } = opts
    if (!appId || !appSecret) return Promise.reject(new Error('appId/appSecret required'))
    try {
      const data = await fetchRatesFromMxnzp(appId, appSecret, timeoutMs)
      // build a partial rates map from API
      const newRates = { ...rates.value }
      for (const item of data) {
        if (!item || !item.from || !item.to || !item.price) continue
        const from = String(item.from).toUpperCase()
        const to = String(item.to).toUpperCase()
        const price = Number(item.price)
        if (!price || price <= 0) continue
        // only handle pairs where one side is CNY
        if (to === 'CNY') {
          // item gives FROM/CNY : 1 FROM = price CNY -> 1 CNY = 1/price FROM
          newRates[from] = +(1 / price).toFixed(6)
        } else if (from === 'CNY') {
          // item gives CNY/TO : 1 CNY = price TO -> rates[TO] = price
          newRates[to] = +price.toFixed(6)
        }
        // handle special case like '100JPY' -> normalize to 'JPY' with price per 1
        // e.g., from '100JPY' to 'CNY' with price p means 100 JPY = p CNY => 1 JPY = p/100 CNY => rates['JPY'] = 1 / (p/100) = 100/p
        if (from && from.match(/^\d+/)) {
          const m = from.match(/^(\d+)([A-Z]+)$/)
          if (m && m.length === 3) {
            const factor = Number(m[1])
            const cur = m[2]
            if (to === 'CNY') newRates[cur] = +(factor / price).toFixed(6)
          }
        }
      }
      rates.value = newRates
      return newRates
    } catch (err) {
      // on failure, do not clobber existing default rates
      return Promise.reject(err)
    }
  }

  const symbol = computed(() => symbols[code.value] || code.value)
  const rateToString = computed(() => `1 ${base} = ${rates.value[code.value]} ${code.value}`)

  return {
    base,
    code,
    symbol,
    rates,
    setCurrency,
    setRates,
    toDisplay,
    toBase,
    format,
    refreshRates,
    rateToString,
  }
}
